/**
 * Factură pe firmă — completare automată a datelor de la ANAF.
 * Se instalează ca `assets/factura-firma.js` în tema District.
 *
 * Clientul tastează doar CUI-ul. Denumirea, sediul social și numărul de la Registrul
 * Comerțului vin de la ANAF prin intermediarul propriu (vezi ../README.md) și se salvează
 * ca atribute de coș, de unde ajung pe comandă.
 *
 * De ce element personalizat, și nu un script în bloc: coșul temei e un sertar care se
 * reîncarcă prin Section Rendering API. Scripturile injectate așa nu rulează, dar elementele
 * personalizate se activează singure de fiecare dată când marcajul reapare în pagină.
 * Din același motiv scriptul se încarcă o dată, din `layout/theme.liquid`.
 *
 * Nimic de aici nu poate bloca o comandă: dacă ANAF tace sau intermediarul e picat,
 * câmpurile rămân editabile de mână și coșul merge mai departe.
 *
 * 15.09.2026 — căutarea pornește SINGURĂ la tastare, cu o pauză de 600 ms, iar CUI-ul se
 * salvează în coș chiar dacă ANAF tace sau clientul nu apasă niciodată butonul. Motivul e
 * un defect reprodus pe comanda #1004: textul promite „restul datelor le luăm de la ANAF”,
 * butonul „Caută” era gri și citea ca dezactivat, iar clientul tasta CUI-ul și pleca la
 * checkout. Pe comandă ajungea „Factură pe firmă: Da” fără niciun CUI — adică exact cazul
 * în care operatorul nu are ce factura.
 */

const CHEIE_CONTROL = '753217532';

/** Aceeași cifră de control ca în worker — verificată aici ca să nu plece cereri degeaba. */
function cuiValid(brut) {
  const cifre = String(brut || '').replace(/\D/g, '');
  if (cifre.length < 2 || cifre.length > 10) return false;
  const control = Number(cifre.slice(-1));
  const corp = cifre.slice(0, -1).padStart(9, '0');
  let suma = 0;
  for (let i = 0; i < 9; i++) suma += Number(corp[i]) * Number(CHEIE_CONTROL[i]);
  let rest = (suma * 10) % 11;
  if (rest === 10) rest = 0;
  return rest === control;
}

const MESAJE = {
  cui_invalid: 'Numărul acesta nu e un CUI valid. Verifică cifrele.',
  negasit: 'Nu am găsit nicio firmă cu acest CUI la ANAF. Verifică numărul sau completează datele mai jos.',
  indisponibil: 'ANAF nu răspunde acum. Completează datele mai jos și comanda merge mai departe.',
  inactiv: 'Atenție: ANAF o arată ca firmă inactivă fiscal.',
  cauta: 'Caut la ANAF…',
};

/** Cât așteptăm după ultima tastă înainte să pornim căutarea. */
const PAUZA_TASTARE = 600;

class FacturaFirma extends HTMLElement {
  connectedCallback() {
    if (this.pornit) return;
    this.pornit = true;

    this.endpoint = this.dataset.endpoint || '';
    this.comutator = this.querySelector('[data-ff-comutator]');
    this.panou = this.querySelector('[data-ff-panou]');
    this.cui = this.querySelector('[data-ff-cui]');
    this.buton = this.querySelector('[data-ff-cauta]');
    this.stare = this.querySelector('[data-ff-stare]');
    this.manuale = this.querySelector('[data-ff-manuale]');
    this.denumire = this.querySelector('[data-ff-denumire]');
    this.adresa = this.querySelector('[data-ff-adresa]');
    this.regcom = this.querySelector('[data-ff-regcom]');

    if (!this.comutator || !this.cui) return;

    this.comutator.addEventListener('change', () => this.comuta());
    this.buton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.cauta();
    });
    this.cui.addEventListener('input', () => this.laTastare());
    this.cui.addEventListener('change', () => this.cauta());
    this.cui.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.cauta();
      }
    });
    [this.denumire, this.adresa, this.regcom].forEach((camp) =>
      camp?.addEventListener('change', () => this.salveaza())
    );

    this.aratePanou(this.comutator.checked);
  }

  /**
   * Pornește singură, la 600 ms după ultima tastă. Dacă numărul e un CUI valid, caută la
   * ANAF (care salvează tot la final). Dacă nu e încă valid — clientul tastează — salvăm
   * măcar ce a scris, ca să nu plece comanda fără CUI.
   */
  laTastare() {
    clearTimeout(this.pauza);
    this.pauza = setTimeout(() => {
      const brut = (this.cui.value || '').trim();
      if (!brut) return;
      if (cuiValid(brut)) {
        this.cauta();
      } else {
        this.salveaza();
      }
    }, PAUZA_TASTARE);
  }

  aratePanou(vizibil) {
    this.panou?.toggleAttribute('hidden', !vizibil);
  }

  arataManuale() {
    this.manuale?.removeAttribute('hidden');
  }

  scrieStare(text, tip) {
    if (!this.stare) return;
    this.stare.textContent = text || '';
    this.stare.dataset.tip = tip || '';
  }

  async comuta() {
    const pornit = this.comutator.checked;
    this.aratePanou(pornit);
    if (pornit) {
      this.cui.focus();
      await this.salveaza();
    } else {
      // Golirea unui atribut îl scoate de pe comandă.
      clearTimeout(this.pauza);
      this.ultimulGasit = null;
      this.scrieStare('', '');
      await this.trimite({
        'Factură pe firmă': '',
        CUI: '',
        Firmă: '',
        'Nr. Reg. Com.': '',
        'Sediu social': '',
      });
    }
  }

  async cauta() {
    const brut = (this.cui.value || '').trim();
    if (!brut) return;

    // Butonul, ieșirea din câmp și tastarea pot cere aceeași căutare. O facem o singură dată,
    // dar numai dacă a reușit — un eșec trebuie să se poată reîncerca.
    if (brut === this.ultimulGasit) return;

    clearTimeout(this.pauza);

    if (!cuiValid(brut)) {
      this.scrieStare(MESAJE.cui_invalid, 'eroare');
      this.arataManuale();
      await this.salveaza();
      return;
    }

    if (!this.endpoint) {
      // Intermediarul nu e configurat încă: mergem mai departe manual, fără să anunțăm clientul.
      this.arataManuale();
      await this.salveaza();
      return;
    }

    this.scrieStare(MESAJE.cauta, 'asteptare');
    let date;
    try {
      const r = await fetch(`${this.endpoint}?cui=${encodeURIComponent(brut)}`, {
        headers: { Accept: 'application/json' },
      });
      date = await r.json();
    } catch (e) {
      date = { ok: false, motiv: 'indisponibil' };
    }

    if (!date || date.ok !== true) {
      this.scrieStare(MESAJE[date?.motiv] || MESAJE.indisponibil, 'eroare');
      this.arataManuale();
      await this.salveaza();
      return;
    }

    this.ultimulGasit = brut;

    if (this.denumire) this.denumire.value = date.denumire || '';
    if (this.adresa) this.adresa.value = date.adresa || '';
    if (this.regcom) this.regcom.value = date.nrRegCom || '';
    this.arataManuale();

    this.scrieStare(date.inactiv ? MESAJE.inactiv : '', date.inactiv ? 'atentie' : '');
    await this.salveaza();
  }

  /**
   * c73, 16.09.2026 — confirmarea vizibila in cos. Cosul se actualizeaza prin /cart/update.js,
   * care NU re-randeaza, deci fara pasul acesta linia ar aparea abia la urmatoarea incarcare de
   * pagina, adica dupa ce clientul a plecat la checkout. Denumirea vine de la ANAF si intra cu
   * textContent, nu interpolata in innerHTML: contine &, ghilimele si, ocazional, <.
   */
  confirma() {
    const el = this.querySelector('[data-ff-confirmare]');
    if (!el) return;
    const firma = (this.denumire?.value || '').trim();
    const cui = (this.cui.value || '').trim().toUpperCase();
    if (!this.comutator.checked || !firma) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.innerHTML = '✓ Factura va fi emisă pe <strong></strong>.<br>La checkout nu mai apar — nu e nevoie să le rescrii.';
    el.querySelector('strong').textContent = cui ? firma + ', CUI ' + cui : firma;
  }

  salveaza() {
    this.confirma();
    return this.trimite({
      'Factură pe firmă': this.comutator.checked ? 'Da' : '',
      CUI: (this.cui.value || '').trim().toUpperCase(),
      Firmă: (this.denumire?.value || '').trim(),
      'Nr. Reg. Com.': (this.regcom?.value || '').trim(),
      'Sediu social': (this.adresa?.value || '').trim(),
    });
  }

  async trimite(atribute) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: atribute }),
      });
    } catch (e) {
      /* Coșul nu se blochează pentru asta. */
    }
  }
}

if (!customElements.get('factura-firma')) {
  customElements.define('factura-firma', FacturaFirma);
}
